/**
 * DynamicForm Component
 * Renders a complete form based on field definitions using Fluent UI
 * This component is stateless and receives data via props
 */

import * as React from 'react';
import {
  Card,
  CardHeader,
  CardPreview,
  Button,
  Text,
  Divider,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Spinner,
  TabList,
  Tab,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import type { SelectTabData, SelectTabEvent, TabValue } from '@fluentui/react-components';
import { SendRegular, ErrorCircleRegular } from '@fluentui/react-icons';
import type { PublicRequestTemplate, FormSection } from '@structured-request/core';
import {
  evaluateCondition,
} from '@structured-request/core';
import { FieldRenderer } from './FieldRenderer.js';

// Collapses multi-column layouts when the viewport is phone-sized.
const useIsMobile = () => {
  const [isMobile, setIsMobile] = React.useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 599px)').matches
  );
  React.useEffect(() => {
    const mq = window.matchMedia('(max-width: 599px)');
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isMobile;
};

const useStyles = makeStyles({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  card: {
    maxWidth: '860px',
    width: '100%',
    minWidth: 0,
  },
  header: {
    paddingBottom: tokens.spacingVerticalM,
  },
  instructions: {
    backgroundColor: tokens.colorNeutralBackground2,
    padding: tokens.spacingVerticalM,
    borderRadius: tokens.borderRadiusMedium,
    marginBottom: tokens.spacingVerticalM,
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: tokens.spacingHorizontalM,
    paddingTop: tokens.spacingVerticalL,
  },
  computedField: {
    backgroundColor: tokens.colorNeutralBackground3,
    padding: tokens.spacingVerticalS,
    borderRadius: tokens.borderRadiusMedium,
    marginBottom: tokens.spacingVerticalM,
  },
  sectionHeader: {
    marginTop: tokens.spacingVerticalL,
    marginBottom: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalXS,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  tabContent: {
  },
  logoBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: `${tokens.borderRadiusMedium} ${tokens.borderRadiusMedium} 0 0`,
    minHeight: '64px',
  },
  logoImg: {
    maxHeight: '48px',
    maxWidth: '160px',
    objectFit: 'contain' as const,
    display: 'block',
  },
  tabScroll: {
    width: '100%',
    overflowX: 'auto',
    // Smooth momentum scrolling on iOS
    WebkitOverflowScrolling: 'touch',
  },
});

export interface DynamicFormProps {
  /** Request template with field definitions */
  template: PublicRequestTemplate;
  /** Current form data */
  data: Record<string, unknown>;
  /** Callback when field value changes */
  onChange: (name: string, value: unknown) => void;
  /** Callback when form is submitted */
  onSubmit: (data: Record<string, unknown>) => void;
  /** Current validation errors */
  errors?: Record<string, string>;
  /** Whether form is currently submitting */
  isSubmitting?: boolean;
  /** Success message to show after submission */
  successMessage?: string;
  /** Error message to show on submission failure */
  errorMessage?: string;
  /** Whether to show the card wrapper (default: true) */
  showCard?: boolean;
  /** Custom submit button text */
  submitLabel?: string;
  /** Additional buttons/actions to render */
  additionalActions?: React.ReactNode;
  /** Whether the form is disabled */
  disabled?: boolean;
  /** When true the form is rendered as a read-only view — all inputs disabled and the submit button hidden */
  readOnly?: boolean;
  /** Context required for presigned file uploads — mspId and a responseId (may be a pending client-generated ID for new submissions) */
  uploadContext?: { mspId: string; responseId: string };
}

export const DynamicForm: React.FC<DynamicFormProps> = ({
  template,
  data,
  onChange,
  onSubmit,
  errors = {},
  isSubmitting = false,
  successMessage,
  errorMessage,
  showCard = true,
  submitLabel = 'Submit',
  additionalActions,
  disabled = false,
  readOnly = false,
  uploadContext,
}) => {
  const styles = useStyles();
  const isMobile = useIsMobile();

  const hasTabs = (template.tabs?.length ?? 0) > 0;
  const sortedTabs = React.useMemo(
    () => (template.tabs ? [...template.tabs].sort((a, b) => a.order - b.order) : []),
    [template.tabs]
  );
  const [activeTab, setActiveTab] = React.useState<TabValue>(
    sortedTabs[0]?.id ?? ''
  );

  const handleTabSelect = (_ev: SelectTabEvent, data: SelectTabData) => {
    setActiveTab(data.value);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(data);
  };

  const gridColumns = template.gridColumns ?? 1;
  const effectiveColumns = isMobile ? 1 : gridColumns;
  const sections: FormSection[] = template.sections ?? [];

  // Sort fields by order
  const sortedFields = React.useMemo(
    () => [...template.fields].sort((a, b) => a.order - b.order),
    [template.fields]
  );

  // Filter visible fields based on conditions
  const visibleFields = React.useMemo(
    () =>
      sortedFields.filter((field) => {
        if (!field.showWhen) return true;
        return evaluateCondition(field.showWhen, data);
      }),
    [sortedFields, data]
  );

  // When tabs are configured, filter fields for the active tab.
  // Fields without a tabId fall into the first tab.
  const firstTabId = sortedTabs[0]?.id ?? '';

  // Determine which tabs have validation errors so we can show indicators.
  const tabsWithErrors = React.useMemo(() => {
    if (!hasTabs || Object.keys(errors).length === 0) return new Set<string>();
    const errorNames = new Set(Object.keys(errors));
    const result = new Set<string>();
    sortedTabs.forEach((tab) => {
      const inTab = visibleFields.filter((f) =>
        f.tabId ? f.tabId === tab.id : tab.id === firstTabId
      );
      if (inTab.some((f) => errorNames.has(f.name))) result.add(tab.id);
    });
    return result;
  }, [hasTabs, errors, sortedTabs, visibleFields, firstTabId]);
  const fieldsForTab = (tabId: string) =>
    visibleFields.filter((f) =>
      f.tabId ? f.tabId === tabId : tabId === firstTabId
    );

  // Determine whether fields use explicit grid positioning
  const hasExplicitPositions = (fields: typeof visibleFields) =>
    fields.some((f) => f.gridX !== undefined || f.gridY !== undefined);

  const renderSingleField = (field: (typeof visibleFields)[0], extraStyle?: React.CSSProperties) => {
    // Spacer fields render as empty grid cells to create blank space in the layout
    if (field.type === 'spacer') {
      return <div key={field.id} style={{ minWidth: 0, ...extraStyle }} aria-hidden="true" />;
    }
    return (
      <div key={field.id} style={{ minWidth: 0, ...extraStyle }}>
        <FieldRenderer
          field={field}
          value={data[field.name]}
          onChange={onChange}
          error={errors[field.name]}
          allErrors={errors}
          disabled={disabled || isSubmitting || readOnly}
          allValues={data}
          uploadContext={uploadContext}
        />
      </div>
    );
  };

  const renderFieldsExplicit = (fields: typeof visibleFields) => {
    // On mobile, explicit grid positions (gridX/gridY) would overlap in a 1-col layout.
    // Fall back to a simple stacked list ordered by the field's `order` value.
    if (isMobile) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM }}>
          {fields.map((field) => renderSingleField(field))}
        </div>
      );
    }
    return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${gridColumns}, 1fr)`,
          gap: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalM}`,
        }}
      >
        {fields.map((field) => {
          const style: React.CSSProperties =
            field.gridX !== undefined
              ? {
                  gridColumn: `${field.gridX + 1} / span ${Math.min(field.gridW ?? 1, gridColumns)}`,
                  gridRow: `${field.gridY! + 1} / span ${field.gridH ?? 1}`,
                }
              : {};
          return renderSingleField(field, style);
        })}
      </div>
    );
  };

  const renderFieldsLegacy = (fields: typeof visibleFields) => (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${effectiveColumns}, 1fr)`,
        gap: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalM}`,
      }}
    >
      {fields.map((field) => {
        const span = isMobile ? 1 : Math.min(field.colSpan ?? 1, gridColumns);
        const style: React.CSSProperties = span > 1 ? { gridColumn: `span ${span}` } : {};
        return renderSingleField(field, style);
      })}
    </div>
  );

  const renderFieldsGrouped = (fields: typeof visibleFields) => {
    // If sections exist, group fields; otherwise use a flat grid
    const sortedSections = [...sections].sort((a, b) => a.order - b.order);
    const useExplicit = hasExplicitPositions(fields);
    const renderFn = useExplicit ? renderFieldsExplicit : renderFieldsLegacy;

    if (sortedSections.length === 0) {
      return renderFn(fields);
    }

    const noSection = fields.filter((f) => !f.sectionId);
    return (
      <>
        {noSection.length > 0 && renderFn(noSection)}
        {sortedSections.map((sec) => {
          const sectionFields = fields.filter((f) => f.sectionId === sec.id);
          if (sectionFields.length === 0) return null;
          return (
            <div key={sec.id}>
              {sec.showTitle && (
                <div className={styles.sectionHeader}>
                  <Text size={400} weight="semibold">{sec.label}</Text>
                </div>
              )}
              {renderFn(sectionFields)}
            </div>
          );
        })}
      </>
    );
  };

  const renderFields = renderFieldsGrouped;

  const renderForm = () => (
    <form onSubmit={handleSubmit} className={styles.form}>
      {errorMessage && (
        <MessageBar intent="error">
          <MessageBarBody>
            <MessageBarTitle>Error</MessageBarTitle>
            {errorMessage}
          </MessageBarBody>
        </MessageBar>
      )}

      {successMessage && (
        <MessageBar intent="success">
          <MessageBarBody>
            <MessageBarTitle>Success</MessageBarTitle>
            {successMessage}
          </MessageBarBody>
        </MessageBar>
      )}

      {template.instructions && (
        <div className={styles.instructions}>
          <Text>{template.instructions}</Text>
        </div>
      )}

      {hasTabs ? (
        <>
          {isMobile ? (
            <select
              value={String(activeTab)}
              onChange={(e) => setActiveTab(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                fontSize: '16px', // 16px prevents iOS auto-zoom on focus
                borderRadius: tokens.borderRadiusMedium,
                border: `1px solid ${tokens.colorNeutralStroke1}`,
                backgroundColor: tokens.colorNeutralBackground1,
                color: tokens.colorNeutralForeground1,
              }}
            >
              {sortedTabs.map((tab) => (
                <option key={tab.id} value={tab.id}>
                  {tabsWithErrors.has(tab.id) ? `⚠ ${tab.label}` : tab.label}
                </option>
              ))}
            </select>
          ) : (
            <div className={styles.tabScroll}>
            <TabList selectedValue={activeTab} onTabSelect={handleTabSelect}>
              {sortedTabs.map((tab) => (
                <Tab key={tab.id} value={tab.id}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    {tab.label}
                    {tabsWithErrors.has(tab.id) && (
                      <ErrorCircleRegular
                        style={{ color: tokens.colorStatusDangerForeground1, fontSize: 14 }}
                        aria-label="This tab has errors"
                      />
                    )}
                  </span>
                </Tab>
              ))}
            </TabList>
            </div>
          )}
          {(() => {
            const inactiveTabs = sortedTabs.filter(
              (t) => t.id !== activeTab && tabsWithErrors.has(t.id)
            );
            return inactiveTabs.length > 0 ? (
              <MessageBar intent="error" style={{ marginTop: tokens.spacingVerticalS }}>
                <MessageBarBody>
                  Please fix errors on:{' '}
                  {inactiveTabs.map((t, i) => (
                    <React.Fragment key={t.id}>
                      <button
                        type="button"
                        onClick={() => setActiveTab(t.id)}
                        style={{
                          background: 'none',
                          border: 'none',
                          padding: 0,
                          cursor: 'pointer',
                          color: tokens.colorStatusDangerForeground1,
                          fontWeight: tokens.fontWeightSemibold,
                          textDecoration: 'underline',
                          fontSize: 'inherit',
                        }}
                      >
                        {t.label}
                      </button>
                      {i < inactiveTabs.length - 1 ? ', ' : ''}
                    </React.Fragment>
                  ))}
                </MessageBarBody>
              </MessageBar>
            ) : null;
          })()}
          <div className={styles.tabContent}>
            {renderFields(fieldsForTab(activeTab as string))}
          </div>
        </>
      ) : (
        renderFields(visibleFields)
      )}

      <div className={styles.actions}>
        {additionalActions}
        {!readOnly && (
          <Button
            appearance="primary"
            type="submit"
            disabled={disabled || isSubmitting}
            icon={isSubmitting ? <Spinner size="tiny" /> : <SendRegular />}
          >
            {isSubmitting ? 'Submitting...' : submitLabel}
          </Button>
        )}
      </div>
    </form>
  );

  if (!showCard) {
    return renderForm();
  }

  return (
    <Card className={styles.card}>
      {(template.primaryLogoUrl || template.secondaryLogoUrl) && (
        <div className={styles.logoBar}>
          <div>
            {template.primaryLogoUrl && (
              <img
                src={template.primaryLogoUrl}
                alt="Primary logo"
                className={styles.logoImg}
              />
            )}
          </div>
          <div>
            {template.secondaryLogoUrl && (
              <img
                src={template.secondaryLogoUrl}
                alt="Secondary logo"
                className={styles.logoImg}
              />
            )}
          </div>
        </div>
      )}
      <CardHeader
        className={styles.header}
        header={<Text size={500} weight="semibold">{template.title}</Text>}
        description={template.description && <Text>{template.description}</Text>}
      />
      <Divider />
      <CardPreview style={{ padding: tokens.spacingVerticalL }}>
        {renderForm()}
      </CardPreview>
    </Card>
  );
};

export default DynamicForm;

